import axios from "axios";

export const TOKEN_STORAGE_KEY = "winkonsupport_token";

export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "/api",
  headers: { "Content-Type": "application/json" },
});

// --- Richiesta: allega sempre il token JWT corrente, se presente ------------
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Risposta: su 401 pulisce il token e notifica l'app ---------------------
// Non importiamo react-router qui (il client API non deve dipendere da React):
// AuthContext si iscrive a questo evento globale e gestisce redirect/stato.
//
// Il 403 `MUST_CHANGE_PASSWORD` è diverso da un 401: il token è valido e NON va
// tolto, altrimenti l'utente perderebbe proprio la sessione con cui deve
// cambiare la password. Serve a correggere lo stato quando il frontend non sa
// che il cambio è obbligatorio: il backend lo sa sempre, ed è lui a dirlo.
// Senza questo ramo l'utente restava nell'app con un toast di errore a ogni
// pagina (#189).
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    } else if (error?.response?.status === 403 && error.response.data?.code === "MUST_CHANGE_PASSWORD") {
      window.dispatchEvent(new CustomEvent("auth:must-change-password"));
    }
    return Promise.reject(error);
  }
);

// --- 413: upload oltre il limite di nginx -----------------------------------
// Sopra il suo client_max_body_size nginx risponde 413 con una pagina HTML sua,
// prima che la richiesta arrivi al backend: niente `error` da mostrare, e senza
// questo ramo l'utente leggeva "Request failed with status code 413".
//
// Il limite citato è quello di Multer per file (backend/src/routes), non quello
// di nginx, che ha 1 MB in più per l'involucro multipart e somma i file: è il
// numero che serve a chi sceglie il file. Ogni location di nginx/default.conf
// supera la somma dei file che Multer accetta, quindi un 413 su queste rotte
// vuol dire che il limite citato è stato superato. Se cambia Multer, va
// cambiato qui. Una rotta non elencata riceve il messaggio senza numero.
const LIMITI_UPLOAD: { prefisso: string; limite: string }[] = [
  { prefisso: "/media", limite: "100 MB" },
  { prefisso: "/catalogo/", limite: "100 MB" },
  { prefisso: "/ritiri/import/", limite: "50 MB per file" },
  { prefisso: "/moduli-rit/", limite: "10 pagine da 10 MB l'una" },
  { prefisso: "/registro-affari/archivi", limite: "20 MB per file" },
  { prefisso: "/contratti/", limite: "20 MB" },
  { prefisso: "/scheda-cliente/", limite: "20 MB" },
  { prefisso: "/import/", limite: "20 MB" },
  { prefisso: "/documenti-cliente", limite: "10 MB" },
  { prefisso: "/documenti-privacy", limite: "10 MB" },
];

function messaggioFileTroppoGrande(url: string | undefined): string {
  const voce = url ? LIMITI_UPLOAD.find((v) => url.startsWith(v.prefisso)) : undefined;
  return voce
    ? `Il file è troppo grande: il massimo ammesso è ${voce.limite}.`
    : "Il file supera la dimensione massima ammessa.";
}

/** Estrae un messaggio d'errore leggibile da una risposta axios/API. */
export function getErrorMessage(err: unknown, fallback = "Si è verificato un errore imprevisto."): string {
  if (axios.isAxiosError(err)) {
    const apiMessage = (err.response?.data as { error?: string } | undefined)?.error;
    if (apiMessage) return apiMessage;
    if (err.response?.status === 413) return messaggioFileTroppoGrande(err.config?.url);
    if (err.message) return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
